import { FC, useMemo, useState } from 'react';

import {
  ActiveDeliveryDatePolicy,
  deliveryDateRelativeLabel,
  formatDeliveryDate,
  formatDeliveryDateShort,
  resolveDeliveryDateChoices,
  toDeliveryDateKey,
  weekdayLabel,
  weekdayOfDate,
} from '@service/giftStore/utils/giftDeliveryDate.util';
import { GiftDesiredDeliveryDateLog } from '@shared/api/giftDelivery/giftDelivery.interface';
import { kstDateOf, kstShiftDay } from '@shared/utils/kstTime.util';

/** 빠른 선택 칩 개수 (오늘 포함). 더 먼 날짜는 날짜 입력 칸으로 */
const QUICK_DAYS = 14;

export interface DesiredDeliveryDateModalTarget {
  deliveryId: string;
  /** 어느 건인지 — '수령인 · 상품명' */
  subtitle: string;
  /** 지금 받는 날 (없으면 새로 지정) */
  currentDate?: string | Date | null;
  /** 구매자가 처음 고른 날짜 (입금 지연으로 자동 변경된 건에만) */
  requestedDate?: string | Date | null;
  logs?: GiftDesiredDeliveryDateLog[];
  /** 일괄배송 건 — 서버가 같은 주문·같은 업체의 다른 배송 건과 주문 원본까지 함께 바꾼다(개별배송은 그 건만) */
  bulk?: boolean;
  /** 이 건 브랜드의 날짜 정책 — 있으면 그 규칙 안에서만 고르고, 규칙 밖은 비밀번호로 강제 지정 */
  policy?: ActiveDeliveryDatePolicy | null;
}

interface Props {
  target: DesiredDeliveryDateModalTarget;
  onClose: () => void;
  /**
   * 저장 — forcePassword가 있으면 규칙 밖 날짜 강제 지정(서버가 비밀번호를 확인한다).
   * 실패하면 throw 하지 말고 호출부가 알림 후 false 를 돌려준다.
   */
  onSave: (date: string, forcePassword?: string) => Promise<boolean>;
}

const ROLE_LABEL: Record<string, string> = {
  admin: '관리자',
  'gift-brand-seller': '배송업체',
};

/**
 * 규칙 밖 날짜인 이유 — 규칙 안이면 ''. 규칙은 주문서와 같다(지금 결제하는 주문 기준):
 * 마감시각 전이면 오늘+마감 전 리드타임부터, 지났으면 마감 후 리드타임부터, 선택 가능 기간까지, 휴무일 제외.
 * 정책이 없는 브랜드는 규칙이 없다.
 */
export const desiredDateRuleViolation = (
  policy: ActiveDeliveryDatePolicy | null | undefined,
  date: string,
  now: Date,
): string => {
  if (!policy || !date) return '';
  const choices = resolveDeliveryDateChoices(policy, now);
  const option = choices.options.find((o) => o.date === date);
  if (option) return option.closed ? '배송 불가일(휴무)입니다.' : '';
  return date < choices.earliestByLead
    ? `지금 기준 가장 빠른 날은 ${formatDeliveryDateShort(choices.earliestByLead)}입니다.`
    : `선택 가능 기간(오늘부터 ${policy.maxDaysAhead}일)을 넘었습니다.`;
};

/**
 * 배송관리 — 받는 날 지정·수정. 구매자는 주문 후 날짜를 화면에서 못 바꾸고 전화로 요청하므로
 * 운영자·배송업체가 여기서 고친다. 기본은 주문서와 같은 규칙 안에서만 고를 수 있고,
 * 규칙 밖 날짜(마감 지난 뒤의 내일 등)는 로그인 계정 비밀번호를 넣어야 강제 지정된다.
 */
export const DesiredDeliveryDateEditModal: FC<Props> = ({ target, onClose, onSave }) => {
  const currentKey = toDeliveryDateKey(target.currentDate);
  const [date, setDate] = useState(currentKey);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const now = useMemo(() => new Date(), []);
  const today = kstDateOf(now);
  const quickDates = useMemo(
    () => Array.from({ length: QUICK_DAYS }, (_, i) => kstShiftDay(today, i)),
    [today],
  );

  const { policy } = target;
  const unchanged = date === currentKey;
  const past = !!date && date < today;
  // 안 바꾼 현재 값은 규칙을 다시 묻지 않는다
  const violation = unchanged || past ? '' : desiredDateRuleViolation(policy, date, now);
  const needsPassword = !!violation;
  const disabled =
    !date || past || unchanged || saving || (needsPassword && !password.trim());

  const selectDate = (next: string) => {
    setDate(next);
    setPassword('');
  };

  const handleSave = async () => {
    if (disabled) return;
    setSaving(true);
    const ok = await onSave(date, needsPassword ? password : undefined);
    if (!ok) setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-label="받는 날 수정"
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
      >
        <h3 className="text-lg font-bold text-gray-900">
          {currentKey ? '받는 날 수정' : '받는 날 지정'}
        </h3>
        <p className="mt-1 break-keep text-xs text-gray-500">{target.subtitle}</p>

        {/* 지금 값 → 바꿀 값 */}
        <div className="mt-4 flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] text-gray-500">현재</p>
            <p className="whitespace-nowrap text-sm font-bold text-gray-700">
              {currentKey ? formatDeliveryDate(currentKey) : '미지정'}
            </p>
          </div>
          <span className="text-gray-400">→</span>
          <div className="min-w-0">
            <p className="text-[11px] text-gray-500">변경</p>
            <p
              className={`whitespace-nowrap text-base font-bold ${
                unchanged || !date ? 'text-gray-400' : 'text-indigo-700'
              }`}
            >
              {date ? formatDeliveryDate(date) : '날짜 선택'}
            </p>
          </div>
        </div>
        {toDeliveryDateKey(target.requestedDate) && (
          <p className="mt-1.5 text-xs text-gray-500">
            구매자가 처음 고른 날: {formatDeliveryDate(target.requestedDate)} (입금 지연
            자동 변경)
          </p>
        )}

        {/* 빠른 선택 — 규칙 밖 날짜는 🔒 (누르면 비밀번호를 묻는다) */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {quickDates.map((d, i) => {
            const selected = d === date;
            const locked = !!desiredDateRuleViolation(policy, d, now);
            return (
              <button
                key={d}
                type="button"
                aria-pressed={selected}
                title={locked ? '규칙 밖 날짜 — 비밀번호 필요' : undefined}
                onClick={() => selectDate(d)}
                className={`flex min-w-[48px] flex-col items-center rounded-lg border px-1.5 py-1.5 leading-tight ${
                  selected
                    ? locked
                      ? 'border-rose-600 bg-rose-600 text-white'
                      : 'border-indigo-600 bg-indigo-600 text-white'
                    : locked
                    ? 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100'
                    : 'border-gray-300 bg-white text-gray-800 hover:bg-indigo-50'
                }`}
              >
                <span className="text-[10px]">
                  {locked ? '🔒' : i <= 2 ? deliveryDateRelativeLabel(d, now) : ' '}
                </span>
                <span className="text-sm font-bold">
                  {Number(d.slice(5, 7))}/{Number(d.slice(8, 10))}
                </span>
                <span className="text-[10px]">{weekdayLabel(weekdayOfDate(d))}</span>
              </button>
            );
          })}
        </div>

        <label className="mt-3 block text-sm font-medium text-gray-700">
          날짜 직접 선택
          <input
            type="date"
            value={date}
            min={today}
            onChange={(e) => selectDate(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        {past && (
          <p className="mt-2 text-xs font-bold text-rose-600">
            지난 날짜는 선택할 수 없습니다.
          </p>
        )}

        {/* 규칙 밖 날짜 — 비밀번호를 넣어야 강제 지정 */}
        {needsPassword && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
            <p className="break-keep text-xs font-bold text-rose-700">
              규칙 밖 날짜 — {violation}
            </p>
            <label className="mt-2 block text-xs font-medium text-gray-700">
              강제 지정하려면 로그인 비밀번호 입력
              <input
                type="password"
                value={password}
                autoComplete="off"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                }}
                className="mt-1 block w-full rounded-md border border-rose-300 bg-white px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
            </label>
          </div>
        )}

        <ul className="mt-3 space-y-0.5 text-xs text-gray-500">
          {target.bulk && <li>· 같은 주문의 같은 업체 배송 건에 함께 적용</li>}
          <li>· 자동 알림 없음 — 변경 내용은 전화·카톡으로 직접 안내</li>
        </ul>

        {!!target.logs?.length && (
          <div className="mt-3 rounded-lg border border-gray-200 p-3">
            <p className="text-[11px] font-semibold text-gray-500">변경 이력</p>
            <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
              {target.logs.map((log, i) => (
                <li key={i}>
                  {formatDeliveryDateShort(log.from) || '미지정'} →{' '}
                  {formatDeliveryDateShort(log.to)}
                  {log.forced && (
                    <span className="ml-1 font-bold text-rose-600">강제</span>
                  )}
                  <span className="ml-1.5 text-gray-400">
                    {formatDeliveryDateShort(log.at)}
                    {log.byRole ? ` · ${ROLE_LABEL[log.byRole] ?? log.byRole}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            disabled={disabled}
            onClick={handleSave}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              needsPassword
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {saving ? '저장 중...' : needsPassword ? '강제 지정' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};
